import React from 'react';
import type { InboundConfigResponse, InboundConfigCreate } from '../../../services/adminService';
import AdminUserDetailSection from './AdminUserDetailSection';

interface AdminUserDetailInboundSectionProps {
  inboundConfig: InboundConfigResponse | null;
  loadingInboundConfig: boolean;
  savingInboundConfig: boolean;
  editingInboundConfig: boolean;
  setEditingInboundConfig: (v: boolean) => void;
  inboundEditForm: InboundConfigCreate;
  setInboundEditForm: React.Dispatch<React.SetStateAction<InboundConfigCreate>>;
  expanded: boolean;
  onToggle: () => void;
  onSave: () => void;
  onReload: () => void;
}

const AdminUserDetailInboundSection: React.FC<AdminUserDetailInboundSectionProps> = ({
  inboundConfig,
  loadingInboundConfig,
  savingInboundConfig,
  editingInboundConfig,
  setEditingInboundConfig,
  inboundEditForm,
  setInboundEditForm,
  expanded,
  onToggle,
  onSave,
  onReload,
}) => {
  const headerButton =
    !editingInboundConfig && expanded ? (
      <button
        onClick={(e) => {
          e.stopPropagation();
          setEditingInboundConfig(true);
        }}
        className="px-3 py-1.5 bg-blue-600/20 hover:bg-blue-600/30 border border-blue-500/30 text-blue-300 hover:text-blue-200 rounded-lg text-xs font-medium transition-colors"
      >
        {inboundConfig ? 'Edit' : 'Create'}
      </button>
    ) : null;

  return (
    <AdminUserDetailSection
      sectionId="inbound"
      title="Inbound Configuration"
      expanded={expanded}
      onToggle={onToggle}
      headerButton={headerButton}
    >
      {loadingInboundConfig ? (
        <div className="flex items-center justify-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-cyan-500"></div>
        </div>
      ) : (
        <>
          {editingInboundConfig ? (
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-300 mb-1">Assistant ID</label>
                <input
                  type="text"
                  value={inboundEditForm.assistant_id}
                  onChange={(e) => setInboundEditForm({ ...inboundEditForm, assistant_id: e.target.value })}
                  className="w-full px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-xs text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  placeholder="Inbound Assistant ID"
                  disabled={savingInboundConfig}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-300 mb-1">Campaign Context (Prompt)</label>
                <textarea
                  rows={6}
                  value={inboundEditForm.campaign_context}
                  onChange={(e) => setInboundEditForm({ ...inboundEditForm, campaign_context: e.target.value })}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-xs text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  placeholder="System prompt to use for inbound calls"
                  disabled={savingInboundConfig}
                />
              </div>
              <div className="flex items-center space-x-3">
                <label className="block text-xs font-medium text-gray-300">Active</label>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={inboundEditForm.is_active}
                    onChange={(e) => setInboundEditForm({ ...inboundEditForm, is_active: e.target.checked })}
                    disabled={savingInboundConfig}
                  />
                  <div
                    className={`w-10 h-5 rounded-full ${inboundEditForm.is_active ? 'bg-cyan-500' : 'bg-gray-600'} transition-colors duration-200`}
                  ></div>
                  <div
                    className={`dot absolute left-0.5 top-0.5 bg-white w-4 h-4 rounded-full transition-transform duration-200 ${inboundEditForm.is_active ? 'translate-x-5' : ''}`}
                  ></div>
                </label>
                <span className="text-xs text-gray-300">{inboundEditForm.is_active ? 'Active' : 'Inactive'}</span>
              </div>
              <div className="flex gap-2 pt-3 border-t border-slate-700/40">
                <button
                  onClick={onSave}
                  disabled={savingInboundConfig}
                  className="px-4 py-1.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg text-xs font-medium hover:from-blue-700 hover:to-blue-800 disabled:opacity-60 transition-all"
                >
                  {savingInboundConfig ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={() => {
                    setEditingInboundConfig(false);
                    onReload();
                  }}
                  className="px-4 py-1.5 bg-white/5 text-gray-300 rounded-lg text-xs font-medium hover:bg-white/10 border border-white/20 transition-colors"
                  disabled={savingInboundConfig}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {inboundConfig ? (
                <>
                  <div>
                    <label className="block text-xs font-medium text-gray-300 mb-1">Assistant ID</label>
                    <div className="text-xs text-gray-300 bg-white/5 px-3 py-1.5 rounded-lg break-all min-h-[2rem] flex items-center">
                      {inboundConfig.assistant_id || 'Not Set'}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-300 mb-1">Campaign Context</label>
                    <div className="bg-white/5 border border-white/10 rounded-lg p-3">
                      <pre className="text-xs text-gray-300 whitespace-pre-wrap font-mono">
                        {inboundConfig.campaign_context || 'Not Set'}
                      </pre>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-300 mb-1">Status</label>
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        inboundConfig.is_active
                          ? 'bg-green-500/20 text-green-300 border border-green-500/30'
                          : 'bg-gray-500/20 text-gray-300 border border-gray-500/30'
                      }`}
                    >
                      {inboundConfig.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </>
              ) : (
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 text-xs text-yellow-100">
                  No inbound configuration exists yet. Click "Create" to add one.
                </div>
              )}
            </div>
          )}
        </>
      )}
    </AdminUserDetailSection>
  );
};

export default AdminUserDetailInboundSection;
